import { zipRecordPermissions } from './recordPermissions'
import type { FgaComputedRelation } from './generated/model'

/**
 * What a caller may do to one supplier: every `can_*` relation the model defines on `supplier`,
 * paired with the camelCase key it is reported under.
 */
export const SUPPLIER_RECORD_PERMISSIONS = [
  ['canView', 'can_view'],
  ['canEdit', 'can_edit'],
  ['canViewTprm', 'can_view_tprm'],
  ['canEditTprm', 'can_edit_tprm'],
  ['canManageTprm', 'can_manage_tprm'],
  ['canViewSubprocessors', 'can_view_subprocessors'],
  ['canEditSubprocessors', 'can_edit_subprocessors'],
  ['canManageSubprocessors', 'can_manage_subprocessors'],
  ['canViewRisks', 'can_view_risks'],
  ['canEditRisks', 'can_edit_risks'],
  ['canManageRisks', 'can_manage_risks'],
  ['canViewAlerts', 'can_view_alerts'],
  ['canEditAlerts', 'can_edit_alerts'],
  ['canViewRelatedRequests', 'can_view_related_requests'],
  ['canViewRelatedAgreements', 'can_view_related_agreements'],
  ['canViewRelatedDocuments', 'can_view_related_documents'],
  ['canViewRelatedSubmissions', 'can_view_related_submissions'],
  ['canEditDocuments', 'can_edit_documents'],
  ['canManageDocuments', 'can_manage_documents'],
  ['canManageInternalContacts', 'can_manage_internal_contacts'],
  ['canManageExternalContacts', 'can_manage_external_contacts'],
  ['canManageProducts', 'can_manage_products'],
  ['canCreateProduct', 'can_create_product'],
  ['canAddAgreement', 'can_add_agreement'],
  ['canAdminister', 'can_administer'],
] as const satisfies readonly (readonly [string, FgaComputedRelation<'supplier'>])[]

export type SupplierRecordPermissionKey = (typeof SUPPLIER_RECORD_PERMISSIONS)[number][0]

export type SupplierRecordPermissions = Record<SupplierRecordPermissionKey, boolean>

/** The answer for a caller with no access, and the seed every result is built from. */
export const NO_SUPPLIER_RECORD_PERMISSIONS: SupplierRecordPermissions = {
  canView: false,
  canEdit: false,
  canViewTprm: false,
  canEditTprm: false,
  canManageTprm: false,
  canViewSubprocessors: false,
  canEditSubprocessors: false,
  canManageSubprocessors: false,
  canViewRisks: false,
  canEditRisks: false,
  canManageRisks: false,
  canViewAlerts: false,
  canEditAlerts: false,
  canViewRelatedRequests: false,
  canViewRelatedAgreements: false,
  canViewRelatedDocuments: false,
  canViewRelatedSubmissions: false,
  canEditDocuments: false,
  canManageDocuments: false,
  canManageInternalContacts: false,
  canManageExternalContacts: false,
  canManageProducts: false,
  canCreateProduct: false,
  canAddAgreement: false,
  canAdminister: false,
}

export const toSupplierRecordPermissions = (allowed: readonly boolean[]): SupplierRecordPermissions =>
  zipRecordPermissions('supplier', SUPPLIER_RECORD_PERMISSIONS, NO_SUPPLIER_RECORD_PERMISSIONS, allowed)
